/**
 * KuCoin Futures WebSocket adapter (Pro public push API).
 *
 * Public market data uses wss://x-push-futures.kucoin.com with no REST token
 * (browser-safe; avoids CORS on bullet-public).
 *
 * @see https://www.kucoin.com/docs-new/websocket-api/base-info/introduction-uta
 * @see https://www.kucoin.com/docs-new/3470222w0 (Ticker)
 */

import type { ExchangeWsAdapter } from "../live-pricing.types";

const PRO_FUTURES_WS_URL = "wss://x-push-futures.kucoin.com";
const RECONNECT_DELAYS_MS = [2_000, 5_000, 10_000, 20_000, 30_000];
const DEFAULT_PING_MS = 30_000;

const COIN_ALIASES: Record<string, string> = { BTC: "XBT" };

function normalizeSymbol(symbol: string): string {
  let upper = symbol.toUpperCase().replace(/-/g, "");
  if (upper.endsWith("USDTM")) return upper;
  if (upper.endsWith("USDT")) upper = upper.slice(0, -4);
  else if (upper.endsWith("M")) upper = upper.slice(0, -1);
  const base = COIN_ALIASES[upper] ?? upper;
  return `${base}USDTM`;
}

interface WelcomeMsg {
  message: string;
  pingInterval?: number;
  sessionId?: string;
}

interface ProTickerData {
  s: string;
  l: string;
  a?: string;
  b?: string;
  M?: number;
}

interface ProTickerPush {
  T: string;
  d: ProTickerData;
}

function isWelcome(raw: unknown): raw is WelcomeMsg {
  return (
    typeof raw === "object" &&
    raw !== null &&
    "message" in raw &&
    (raw as WelcomeMsg).message === "welcome"
  );
}

function isFuturesTickerPush(raw: unknown): raw is ProTickerPush {
  return (
    typeof raw === "object" &&
    raw !== null &&
    "T" in raw &&
    (raw as ProTickerPush).T === "ticker.FUTURES" &&
    "d" in raw &&
    typeof (raw as ProTickerPush).d === "object" &&
    (raw as ProTickerPush).d !== null
  );
}

function parsePrice(s: string | undefined): number {
  if (s === undefined) return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

/** Pro API futures ticker uses nanosecond timestamps in M. */
function proTimestampNsToMs(ns: number | undefined): number {
  if (ns === undefined || !Number.isFinite(ns)) return Date.now();
  return Math.floor(ns / 1_000_000);
}

const textDecoder = new TextDecoder();

/**
 * KuCoin Pro WS sends JSON as binary UTF-8 frames; `JSON.parse` only works on strings.
 */
async function messagePayloadToString(data: unknown): Promise<string | null> {
  if (typeof data === "string") return data;
  if (data instanceof ArrayBuffer) return textDecoder.decode(data);
  if (ArrayBuffer.isView(data)) {
    return textDecoder.decode(
      new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
    );
  }
  if (data instanceof Blob) return data.text();
  return null;
}

export const kucoinFuturesWsAdapter: ExchangeWsAdapter = {
  subscribe(symbol, onUpdate, onError) {
    const normalized = normalizeSymbol(symbol);
    let ws: WebSocket | null = null;
    let pingTimer: ReturnType<typeof setInterval> | null = null;
    let stopped = false;
    let attempt = 0;
    let subscribed = false;

    function clearPing() {
      if (pingTimer !== null) {
        clearInterval(pingTimer);
        pingTimer = null;
      }
    }

    function sendUnsubscribe(socket: WebSocket) {
      socket.send(
        JSON.stringify({
          id: Date.now().toString(),
          action: "unsubscribe",
          channel: "ticker",
          symbol: normalized,
          tradeType: "FUTURES",
        })
      );
    }

    function subscribeTicker(socket: WebSocket) {
      socket.send(
        JSON.stringify({
          id: Date.now().toString(),
          action: "subscribe",
          channel: "ticker",
          symbol: normalized,
          tradeType: "FUTURES",
        })
      );
      subscribed = true;
    }

    function handleDecodedJson(raw: unknown) {
      if (isWelcome(raw)) {
        clearPing();
        const interval = raw.pingInterval ?? DEFAULT_PING_MS;
        pingTimer = setInterval(() => {
          ws?.send(JSON.stringify({ id: Date.now().toString(), type: "ping" }));
        }, interval);

        if (!stopped && ws?.readyState === WebSocket.OPEN) {
          subscribeTicker(ws);
        }
        return;
      }

      if (!isFuturesTickerPush(raw)) return;

      const d = raw.d;
      if (d.s !== normalized) return;

      const last = parsePrice(d.l);
      if (last <= 0) return;

      const bid = parsePrice(d.b);
      const ask = parsePrice(d.a);
      const mid = bid > 0 && ask > 0 ? (bid + ask) / 2 : last;

      attempt = 0;
      onUpdate({
        symbol: normalized,
        exchange: "kucoin",
        markPrice: last,
        indexPrice: mid,
        fundingRate: 0,
        nextFundingTime: 0,
        timestamp: proTimestampNsToMs(d.M),
      });
    }

    function connect() {
      if (stopped) return;

      subscribed = false;
      ws = new WebSocket(PRO_FUTURES_WS_URL);
      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        // Subscription must run after welcome (see Pro WS introduction).
      };

      ws.onmessage = (e: MessageEvent) => {
        void messagePayloadToString(e.data).then((text) => {
          if (text === null || stopped) return;
          try {
            handleDecodedJson(JSON.parse(text));
          } catch {
            // ignore parse errors
          }
        });
      };

      ws.onerror = () => {
        // followed by close
      };

      ws.onclose = () => {
        clearPing();
        if (stopped) return;
        const delay =
          RECONNECT_DELAYS_MS[
            Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)
          ];
        attempt++;
        if (attempt > RECONNECT_DELAYS_MS.length) {
          onError?.(new Error(`KuCoin WS closed after ${attempt} attempts`));
          return;
        }
        setTimeout(() => connect(), delay);
      };
    }

    connect();

    return () => {
      stopped = true;
      clearPing();
      if (ws?.readyState === WebSocket.OPEN && subscribed) {
        try {
          sendUnsubscribe(ws);
        } catch {
          // ignore
        }
      }
      ws?.close();
      ws = null;
    };
  },
};
